import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { PcnFieldConfirmation } from '../../viewmodels/pcn-field-confirmation.model';
import { ChargeViewModel } from '../../viewmodels/chargeViewModel';

// Shows one or more unresolved-field confirmations (e.g. "Current Charge could not be
// automatically determined") in a single blocking modal, and lets the user pick a value for each before
// resubmitting. Deliberately has no cancel/dismiss option while every confirmation still has real
// options to choose from: the PCN must not be saved with a guessed default, so there is nothing
// useful to do except pick a real value for every entry. A Close action only appears (see
// hasUnresolvableConfirmation) when at least one confirmation has no options at all - a genuine
// configuration gap the user cannot resolve from here, so trapping them behind an always-disabled
// Confirm button would serve no purpose.
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'pcn-confirmation-modal',
  templateUrl: './pcn-confirmation-modal.component.html',
  styleUrls: ['./pcn-confirmation-modal.component.scss'],
})
export class PcnConfirmationModalComponent implements OnChanges {
  @Input() confirmations: PcnFieldConfirmation[] = [];
  @Output() confirmed = new EventEmitter<Record<string, ChargeViewModel>>();
  // Emitted only when at least one confirmation has no selectable options (see
  // hasUnresolvableConfirmation) - there is nothing to pick in that case, so the only sensible
  // action is to let the user leave rather than trap them behind a permanently-disabled Confirm.
  @Output() closed = new EventEmitter<void>();

  // Keyed by PcnFieldConfirmation.fieldName, bound to each entry's combobox.
  selectedChargeIds: Record<string, number> = {};

  // Shown when the user tries to leave (titlebar X) without having picked a value, while real
  // options are still available - see onTitlebarClose(). Cleared below once allResolved, so it
  // never lingers after the user actually does make a selection.
  showMustSelectMessage = false;

  get hasUnresolvableConfirmation(): boolean {
    return this.confirmations.some((c) => !c.chargeOptions?.length);
  }

  get allResolved(): boolean {
    return (
      this.confirmations.length > 0 &&
      this.confirmations.every((c) => (this.selectedChargeIds[c.fieldName] ?? 0) > 0)
    );
  }

  // The component instance is never destroyed between showings (only the inner kendo-dialog is
  // *ngIf-gated in the template), so a stale selection from a previous showing must be cleared
  // explicitly whenever a new confirmation set arrives - otherwise a leftover id could make
  // allResolved true, or onConfirm() silently resolve to undefined, without the user having chosen
  // anything for the new set.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['confirmations']) {
      this.selectedChargeIds = {};
      this.showMustSelectMessage = false;
    }
  }

  onConfirm(): void {
    if (!this.allResolved) {
      return;
    }

    const selections: Record<string, ChargeViewModel> = {};
    this.confirmations.forEach((c) => {
      selections[c.fieldName] = (c.chargeOptions || []).find(
        (option) => option.id === this.selectedChargeIds[c.fieldName]
      );
    });

    this.confirmed.emit(selections);
  }

  onClose(): void {
    this.closed.emit();
  }

  // kendo-dialog-titlebar always renders its own close "X" (there is no input to suppress it,
  // unlike our footer Close button which only renders *ngIf hasUnresolvableConfirmation) and fires
  // its own separate `close` output. Route it through the same rule as the footer button rather
  // than leaving it as a silent no-op: force the user through selection while real options exist,
  // and only let it act as Close once there is genuinely nothing left to pick.
  onTitlebarClose(): void {
    if (this.hasUnresolvableConfirmation) {
      this.onClose();
    } else {
      this.showMustSelectMessage = true;
    }
  }
}
